<template>
  <div class="filter-field">
    <label>Страна</label>
    <DxSelectBox
      :value="country"
      :items="countryItems"
      value-expr="value"
      display-expr="text"
      :search-enabled="true"
      search-mode="contains"
      :search-expr="['text', 'value']"
      :show-clear-button="false"
      width="160"
      @value-changed="onCountry"
    />
  </div>
  <div class="filter-field">
    <label>Язык</label>
    <DxSelectBox
      :value="language"
      :items="languageItems"
      value-expr="value"
      display-expr="text"
      :search-enabled="true"
      search-mode="contains"
      :search-expr="['text', 'value']"
      width="140"
      @value-changed="onLanguage"
    />
  </div>
  <div class="filter-field">
    <label>Устройство</label>
    <DxSelectBox
      :value="deviceType"
      :items="deviceItems"
      value-expr="value"
      display-expr="text"
      :search-enabled="true"
      search-mode="contains"
      :search-expr="['text', 'value']"
      width="160"
      @value-changed="onDevice"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { DxSelectBox } from 'devextreme-vue/select-box';
import { statsApi } from '../api';
import { countryFlagEmoji } from '../utils/clickLogUi';

type SelectItem = { value: string; text: string };

const ALL: SelectItem = { value: '', text: 'Все' };

const props = defineProps<{
  country: string;
  language: string;
  deviceType: string;
  linkId?: number | null;
  targetUrl?: string | null;
  from?: string;
  to?: string;
}>();

const emit = defineEmits<{
  (e: 'update:country', value: string): void;
  (e: 'update:language', value: string): void;
  (e: 'update:deviceType', value: string): void;
  (e: 'change'): void;
}>();

const countries = ref<string[]>([]);
const languages = ref<string[]>([]);
const deviceTypes = ref<string[]>([]);

const countryItems = computed<SelectItem[]>(() => [
  ALL,
  ...countries.value.map((code) => {
    const flag = countryFlagEmoji(code);
    return { value: code, text: flag ? `${flag} ${code}` : code };
  }),
]);

const languageItems = computed<SelectItem[]>(() => [
  ALL,
  ...languages.value.map((code) => ({ value: code, text: code })),
]);

const deviceItems = computed<SelectItem[]>(() => [
  ALL,
  ...deviceTypes.value.map((code) => ({ value: code, text: code })),
]);

async function loadOptions() {
  const range = { from: props.from, to: props.to };
  const linkId = props.linkId;
  const targetUrl = (props.targetUrl || '').trim();
  try {
    let c: { items?: string[] };
    let l: { items?: string[] };
    let d: { items?: string[] };
    if (linkId && Number.isFinite(linkId)) {
      [c, l, d] = await Promise.all([
        statsApi.listLinkCountries(linkId, range),
        statsApi.listLinkLanguages(linkId, range),
        statsApi.listLinkDeviceTypes(linkId, range),
      ]);
    } else if (targetUrl) {
      const scoped = { target_url: targetUrl, ...range };
      [c, l, d] = await Promise.all([
        statsApi.listTargetCountries(scoped),
        statsApi.listTargetLanguages(scoped),
        statsApi.listTargetDeviceTypes(scoped),
      ]);
    } else {
      countries.value = [];
      languages.value = [];
      deviceTypes.value = [];
      return;
    }
    countries.value = c.items || [];
    languages.value = l.items || [];
    deviceTypes.value = d.items || [];
    let cleared = false;
    if (props.country && !countries.value.includes(props.country)) {
      emit('update:country', '');
      cleared = true;
    }
    if (props.language && !languages.value.includes(props.language)) {
      emit('update:language', '');
      cleared = true;
    }
    if (props.deviceType && !deviceTypes.value.includes(props.deviceType)) {
      emit('update:deviceType', '');
      cleared = true;
    }
    if (cleared) emit('change');
  } catch {
    countries.value = [];
    languages.value = [];
    deviceTypes.value = [];
  }
}

watch(
  () => [props.linkId, props.targetUrl, props.from, props.to] as const,
  () => {
    void loadOptions();
  },
  { immediate: true },
);

function onCountry(e: { value?: string | null; event?: Event }) {
  if (!e.event) return;
  emit('update:country', e.value ?? '');
  emit('change');
}

function onLanguage(e: { value?: string | null; event?: Event }) {
  if (!e.event) return;
  emit('update:language', e.value ?? '');
  emit('change');
}

function onDevice(e: { value?: string | null; event?: Event }) {
  if (!e.event) return;
  emit('update:deviceType', e.value ?? '');
  emit('change');
}
</script>

<style scoped>
.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-field label {
  font-size: 12px;
  color: var(--dx-color-text-secondary, #666);
}
</style>
